import React from 'react'
import { Alert, Box, Button, CircularProgress, Fade, Typography } from '@mui/material'
import { Lock, Security, Verified } from '@mui/icons-material'

type Props = {
  isConnectingBank: boolean
  paymentError: string | null
  onClearPaymentError: () => void
  onStripeConnect: () => void
}

const ConnectBankStep: React.FC<Props> = ({
  isConnectingBank,
  paymentError,
  onClearPaymentError,
  onStripeConnect,
}) => {
  return (
    <Box>
      <Fade in timeout={800} style={{ transitionDelay: '400ms' }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: 'grey.800',
              mb: 2,
              fontSize: {
                xs: '1.75rem',
                sm: '2rem',
                md: '2.25rem',
              },
            }}
          >
            Connect your Bank through Stripe
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'grey.600',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.6,
            }}
          >
            When your patient pay, we deposit the amount to your preferred bank account. We utilize stripe
            to connect with your preferred bank. Setting up Stripe is a quick process, taking about 5
            minutes.
          </Typography>
        </Box>
      </Fade>

      <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Button
            onClick={onStripeConnect}
            variant="contained"
            disabled={isConnectingBank}
            startIcon={
              isConnectingBank ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#635BFF',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                  }}
                >
                  S
                </Box>
              )
            }
            sx={{
              bgcolor: '#635BFF',
              px: 4,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 200,
              '&:hover': {
                bgcolor: '#5A52E8',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(99, 91, 255, 0.3)',
              },
              '&:disabled': {
                bgcolor: '#9ca3af',
                transform: 'none',
                boxShadow: 'none',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {isConnectingBank ? 'Connecting...' : 'Connect with Stripe'}
          </Button>
        </Box>
      </Fade>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
          mt: 3,
          pt: 2,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Security sx={{ color: '#10b981', fontSize: '1rem' }} />
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            SSL Secured
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Verified sx={{ color: '#10b981', fontSize: '1rem' }} />
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            PCI Compliant
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Lock sx={{ color: '#10b981', fontSize: '1rem' }} />
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Bank-Level Security
          </Typography>
        </Box>
      </Box>

      {paymentError && (
        <Fade in timeout={300}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Alert
              severity="error"
              onClose={onClearPaymentError}
              sx={{
                maxWidth: 500,
                width: '100%',
              }}
            >
              {paymentError}
            </Alert>
          </Box>
        </Fade>
      )}
    </Box>
  )
}

export default ConnectBankStep

